import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { Button, FormControl, FormHelperText, MenuItem, Select, TextField } from "@mui/material";
import { hashSync } from "m360-react-components";
import "../AddWebsiteComponent/style.css";
import { PAGE_BUILDER_ENDPOINTS } from "../../core/constants";
import { encodeToBase64, showErrorToast, showSuccessToast } from "../../core/Utils";

const ADD_PAGE_TEMPLATE_AK = process.env.REACT_APP_ADD_PAGE_TEMPLATE_AK;
const MICROSITE_NAME_REGEX = /^[A-Za-z0-9]+$/;
const MSID_REGEX = /^\d+$/;
const SLUG_PATH_REGEX = /^\/?[A-Za-z0-9/_-]+$/;
const DEFAULT_REDIRECTION_TYPE = 301;
const SAVE_MICROSITE_ACTION = "save-microsite";
const REDIRECTION_MASTER_KEYS = [
    "urlRedirectionMaster",
    "urlRedirectionMasters",
    "urlRedirectionMasterList",
    "urlRedirectionData",
    "urlRedirectionList",
    "redirectionMaster",
    "redirectionMasters",
    "redirectionData",
    "redirectionList",
    "redirectMaster",
    "redirectMasters",
    "redirectList"
];

const INITIAL_FORM_DATA = {
    name: "",
    publishstoryMSID: "",
    shortUrls: "",
    status: 1,
    pageTemplateId: ""
};

const DUPLICATE_MICROSITE_NAME_ERROR = "Microsite name already exists. Please choose a different name.";
const DUPLICATE_MICROSITE_URL_ERROR = "URL is already taken. Try a new one.";

const normalizePathForComparison = (value) => {
    if (value == null) {
        return "";
    }

    let normalizedValue = String(value).trim();
    if (!normalizedValue) {
        return "";
    }

    if (!normalizedValue.startsWith("http://") && !normalizedValue.startsWith("https://") && !normalizedValue.startsWith("/")) {
        normalizedValue = `/${normalizedValue}`;
    }

    if (normalizedValue.length > 1 && normalizedValue.endsWith("/")) {
        normalizedValue = normalizedValue.slice(0, -1);
    }

    return normalizedValue;
};

const splitUrlEntries = (value) => {
    return String(value || "")
        .split(/[\n,]+/)
        .map((item) => String(item || "").trim())
        .filter((item) => item !== "");
};

const splitShortUrls = (value) => {
    return splitUrlEntries(value)
        .map((item) => normalizePathForComparison(item))
        .filter((item) => item !== "");
};

const normalizeNameForComparison = (value) => {
    return String(value || "")
        .trim()
        .replace(/\s+/g, " ")
        .toLowerCase();
};

const isSlugOnlyPath = (value) => {
    const trimmedValue = String(value || "").trim();

    if (!trimmedValue) {
        return false;
    }

    if (/https?:\/\//i.test(trimmedValue) || /www\./i.test(trimmedValue)) {
        return false;
    }

    if (/[?#.\s]/.test(trimmedValue)) {
        return false;
    }

    return SLUG_PATH_REGEX.test(trimmedValue);
};

const addPathToMap = (pathMap, value) => {
    const normalizedPath = normalizePathForComparison(value);
    if (normalizedPath && !pathMap.has(normalizedPath)) {
        pathMap.set(normalizedPath, value);
    }
};

const addMultiplePathsToMap = (pathMap, value) => {
    if (Array.isArray(value)) {
        value.forEach((item) => addPathToMap(pathMap, item));
        return;
    }

    splitShortUrls(value).forEach((item) => addPathToMap(pathMap, item));
};

const getTemplatePaths = (template) => {
    const pathMap = new Map();

    addPathToMap(pathMap, template?.destinationPath);
    addPathToMap(pathMap, template?.seoPath);
    addPathToMap(pathMap, template?.sourcePath);
    addPathToMap(pathMap, template?.path);
    addMultiplePathsToMap(pathMap, template?.shortUrls);

    return Array.from(pathMap.keys());
};

const getTemplateShortUrlPaths = (template) => {
    return splitShortUrls(template?.shortUrls);
};

const getRedirectionMasterEntries = (siteResourceData) => {
    for (const key of REDIRECTION_MASTER_KEYS) {
        if (Object.prototype.hasOwnProperty.call(siteResourceData || {}, key) && Array.isArray(siteResourceData?.[key])) {
            return {
                hasRedirectionMaster: true,
                entries: siteResourceData[key]
            };
        }
    }

    for (const key of REDIRECTION_MASTER_KEYS) {
        const nestedValue = siteResourceData?.[key]?.data || siteResourceData?.[key]?.records || siteResourceData?.[key]?.items;
        if (Array.isArray(nestedValue)) {
            return {
                hasRedirectionMaster: true,
                entries: nestedValue
            };
        }
    }

    return {
        hasRedirectionMaster: false,
        entries: []
    };
};

const extractRedirectionSourcePathsFromEntries = (entries = []) => {
    return Array.from(new Set(
        entries
            .map((entry) => normalizePathForComparison(entry?.sourcePath))
            .filter((sourcePath) => sourcePath !== "")
    ));
};

const buildRedirectionLookupApiUrl = ({ clientId, websiteId, sourcePath }) => {
    const lookupEndpoint = PAGE_BUILDER_ENDPOINTS.HEADER_DATA_API.replace(/\/getsiteResources$/, "/rendering/getDestinationPath");
    const queryParams = new URLSearchParams({
        clientId: String(clientId),
        websiteId: String(websiteId),
        sourcePath: String(sourcePath)
    });

    return `${lookupEndpoint}?${queryParams.toString()}`;
};

const buildNonceHeaderFromApiUrl = (apiUrl) => {
    const queryString = apiUrl.includes("?") ? `?${apiUrl.split("?")[1]}` : "";

    if (!queryString) {
        return "";
    }

    return hashSync(queryString, 6);
};

const findTemplatesByShortUrls = (pageTemplates, shortUrlPaths) => {
    const matchedTemplates = [];
    const templateIds = new Set();

    pageTemplates.forEach((template) => {
        const hasMatchingShortUrl = shortUrlPaths.some((path) => getTemplateShortUrlPaths(template).includes(path));
        const templateId = String(template?.pageId || `${template?.name}-${template?.type}`);

        if (hasMatchingShortUrl && !templateIds.has(templateId)) {
            templateIds.add(templateId);
            matchedTemplates.push(template);
        }
    });

    return matchedTemplates;
};

const findTemplatesByPaths = (pageTemplates, paths) => {
    const matchedTemplates = [];
    const templateIds = new Set();

    pageTemplates.forEach((template) => {
        const templatePaths = getTemplatePaths(template);
        const hasMatchingPath = paths.some((path) => templatePaths.includes(path));
        const templateId = String(template?.pageId || `${template?.name}-${template?.type}`);

        if (hasMatchingPath && !templateIds.has(templateId)) {
            templateIds.add(templateId);
            matchedTemplates.push(template);
        }
    });

    return matchedTemplates;
};

const findConflictingTemplatePath = ({ paths, pageTemplates, excludedPageId }) => {
    for (const path of paths) {
        const templateConflict = pageTemplates.find((template) => {
            if (excludedPageId != null && String(template?.pageId) === String(excludedPageId)) {
                return false;
            }

            return getTemplatePaths(template).includes(path);
        });

        if (templateConflict) {
            return path;
        }
    }

    return null;
};

const isListPageTemplate = (template) => {
    const templateIdentifier = `${template?.name || ""} ${template?.type || ""}`.toLowerCase();
    return templateIdentifier.includes("list")
        || templateIdentifier.includes("homepage")
        || templateIdentifier.includes("home page");
};

const getListPageTemplates = (pageTemplates) => {
    return pageTemplates.filter((template) => isListPageTemplate(template));
};

const getSelectedListTemplate = (listPageTemplates, selectedPageTemplate, selectedTemplateId) => {
    if (selectedTemplateId) {
        const matchedTemplate = listPageTemplates.find((template) => String(template?.pageId) === String(selectedTemplateId));
        if (matchedTemplate) {
            return matchedTemplate;
        }
    }

    if (selectedPageTemplate && isListPageTemplate(selectedPageTemplate)) {
        return selectedPageTemplate;
    }

    return listPageTemplates[0] || null;
};

const validateMicrositeName = (value) => {
    const trimmedValue = String(value || "").trim();

    if (!trimmedValue) {
        return "Please enter microsite name.";
    }

    if (!MICROSITE_NAME_REGEX.test(trimmedValue)) {
        return "Microsite name must be a single word using only letters and numbers. Do not use spaces. Example: TimesOfIndia.";
    }

    return "";
};

const validatePublishstoryMsid = (value) => {
    const trimmedValue = String(value || "").trim();

    if (!trimmedValue) {
        return "Please enter Publishstory MSID.";
    }

    if (!MSID_REGEX.test(trimmedValue)) {
        return "Publishstory MSID must be numeric only.";
    }

    return "";
};

const parseMicrositeUrls = (value) => {
    const rawUrlEntries = splitUrlEntries(value);

    if (rawUrlEntries.length === 0) {
        return {
            errorMessage: "Please enter a URL slug."
        };
    }

    if (rawUrlEntries.length > 1) {
        return {
            errorMessage: "Please enter only one URL slug, for example /panamapapers."
        };
    }

    const invalidUrlEntry = rawUrlEntries.find((entry) => !isSlugOnlyPath(entry));
    if (invalidUrlEntry) {
        return {
            errorMessage: `Invalid URL slug: ${invalidUrlEntry}. Enter slug/path only without https, www, or domain.`
        };
    }

    const inputPaths = rawUrlEntries
        .map((entry) => normalizePathForComparison(entry))
        .filter((entry) => entry !== "");
    const [destinationPath = ""] = inputPaths;
    const shortUrlPaths = destinationPath ? [destinationPath] : [];

    if (!destinationPath) {
        return {
            errorMessage: "Please enter a valid URL slug."
        };
    }

    const duplicatePath = inputPaths.find((path, index) => inputPaths.indexOf(path) !== index);
    if (duplicatePath) {
        return {
            errorMessage: `Duplicate URL found: ${duplicatePath}`
        };
    }

    const formPathMap = new Map();
    inputPaths.forEach((path) => addPathToMap(formPathMap, path));

    if (formPathMap.size !== inputPaths.length) {
        return {
            errorMessage: "URLs must be unique."
        };
    }

    return {
        errorMessage: "",
        inputPaths,
        destinationPath,
        shortUrlPaths
    };
};

const extractPageTemplateNamesFromPageTemplates = (templates = []) => {
    return Array.from(new Set(
        templates
            .map((template) => String(template?.name || "").trim())
            .filter((name) => name !== "")
    ));
};

const extractPageTemplateNamesFromThemeResponse = (themeResponse = []) => {
    return Array.from(new Set(
        (Array.isArray(themeResponse) ? themeResponse : [])
            .flatMap((theme) => Array.isArray(theme?.pageTemplateList) ? theme.pageTemplateList : [])
            .map((template) => String(template?.name || "").trim())
            .filter((name) => name !== "")
    ));
};

const getDuplicateMicrositeNameError = ({ value, existingNames = [], allowedExistingName = "" }) => {
    const normalizedInputName = normalizeNameForComparison(value);
    const normalizedAllowedName = normalizeNameForComparison(allowedExistingName);

    if (!normalizedInputName) {
        return "";
    }

    const hasDuplicate = existingNames.some((name) => {
        const normalizedExistingName = normalizeNameForComparison(name);

        if (!normalizedExistingName) {
            return false;
        }

        if (normalizedAllowedName && normalizedExistingName === normalizedAllowedName) {
            return false;
        }

        return normalizedExistingName === normalizedInputName;
    });

    return hasDuplicate ? DUPLICATE_MICROSITE_NAME_ERROR : "";
};

const hasPathConflict = (inputPath, existingPath) => {
    const normalizedInputPath = normalizePathForComparison(inputPath);
    const normalizedExistingPath = normalizePathForComparison(existingPath);

    if (!normalizedInputPath || !normalizedExistingPath) {
        return false;
    }

    return normalizedInputPath === normalizedExistingPath
        || normalizedInputPath.startsWith(`${normalizedExistingPath}/`)
        || normalizedExistingPath.startsWith(`${normalizedInputPath}/`);
};

const getDuplicateMicrositeUrlError = ({ value, existingSourcePaths = [] }) => {
    const parsedUrls = parseMicrositeUrls(value);
    if (parsedUrls.errorMessage || !parsedUrls.destinationPath) {
        return "";
    }

    const normalizedSourcePath = normalizePathForComparison(parsedUrls.destinationPath);
    const conflictingSourcePath = existingSourcePaths.find((sourcePath) => hasPathConflict(normalizedSourcePath, sourcePath));

    return conflictingSourcePath ? DUPLICATE_MICROSITE_URL_ERROR : "";
};

const buildMicrositeTemplateSegment = (value) => {
    return String(value || "")
        .trim()
        .replace(/[^A-Za-z0-9]/g, "");
};

// Microsite pageType must always be "articlelist" (used in destinationPath generation).
const getMicrositeRouteType = () => "articlelist";

const extractApiErrorMessage = (error, fallbackMessage) => {
    const responseData = error?.response?.data;

    if (typeof responseData === "string" && responseData.trim()) {
        return responseData.trim();
    }

    if (responseData?.message) {
        return responseData.message;
    }

    if (responseData?.error) {
        return responseData.error;
    }

    if (Array.isArray(responseData?.errors) && responseData.errors.length > 0) {
        const firstError = responseData.errors[0];
        if (typeof firstError === "string") {
            return firstError;
        }
        if (firstError?.message) {
            return firstError.message;
        }
    }

    if (error?.message) {
        return error.message;
    }

    return fallbackMessage;
};

export const AddMicrositeComponent = (props) => {
    const { handleCloseOverlay, onMicrositeCreated, pageTemplates = [], selectedPageTemplate, selectedWebsite } = props;
    const [formData, setFormData] = useState(INITIAL_FORM_DATA);
    const [isSaving, setIsSaving] = useState(false);
    const isSaveButtonPointerDownRef = useRef(false);
    const [existingPageTemplateNames, setExistingPageTemplateNames] = useState(() => extractPageTemplateNamesFromPageTemplates(pageTemplates));
    const [hasLoadedExistingPageTemplateNames, setHasLoadedExistingPageTemplateNames] = useState(false);
    const [isLoadingExistingPageTemplateNames, setIsLoadingExistingPageTemplateNames] = useState(false);
    const [allowedExistingMicrositeName, setAllowedExistingMicrositeName] = useState("");
    const [existingRedirectionEntries, setExistingRedirectionEntries] = useState([]);
    const [existingRedirectionSourcePaths, setExistingRedirectionSourcePaths] = useState([]);
    const [hasLoadedExistingRedirectionEntries, setHasLoadedExistingRedirectionEntries] = useState(false);
    const [isLoadingExistingRedirectionEntries, setIsLoadingExistingRedirectionEntries] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({
        name: "",
        publishstoryMSID: "",
        shortUrls: "",
        pageTemplateId: ""
    });
    const [fieldTouched, setFieldTouched] = useState({});
    const [urlHelperMessage, setUrlHelperMessage] = useState("");
    const [isCheckingUrls, setIsCheckingUrls] = useState(false);
    const listPageTemplates = getListPageTemplates(pageTemplates);
    const selectedTemplate = getSelectedListTemplate(listPageTemplates, selectedPageTemplate, formData.pageTemplateId);

    const loadExistingPageTemplateNames = async () => {
        if (!selectedWebsite?.clientId || !selectedWebsite?.websiteId) {
            return extractPageTemplateNamesFromPageTemplates(pageTemplates);
        }

        setIsLoadingExistingPageTemplateNames(true);

        try {
            const response = await axios({
                method: "GET",
                url: `${PAGE_BUILDER_ENDPOINTS.DOMAIN}/pageBuilder/getThemeByClientAndWebsite?clientId=${selectedWebsite.clientId}&websiteId=${selectedWebsite.websiteId}&env=1`,
                withCredentials: true
            });

            const pageTemplateNames = extractPageTemplateNamesFromThemeResponse(response.data);
            setExistingPageTemplateNames(pageTemplateNames);
            setHasLoadedExistingPageTemplateNames(true);
            return pageTemplateNames;
        } catch (error) {
            const fallbackNames = extractPageTemplateNamesFromPageTemplates(pageTemplates);
            setExistingPageTemplateNames(fallbackNames);
            setHasLoadedExistingPageTemplateNames(false);
            throw new Error(extractApiErrorMessage(error, "Unable to validate microsite name right now."));
        } finally {
            setIsLoadingExistingPageTemplateNames(false);
        }
    };

    const loadExistingRedirectionEntries = async () => {
        if (!selectedWebsite?.clientId || !selectedWebsite?.websiteId) {
            setExistingRedirectionEntries([]);
            setExistingRedirectionSourcePaths([]);
            return {
                entries: [],
                sourcePaths: []
            };
        }

        setIsLoadingExistingRedirectionEntries(true);

        try {
            const response = await axios({
                method: "GET",
                url: `${PAGE_BUILDER_ENDPOINTS.HEADER_DATA_API}?clientId=${selectedWebsite.clientId}&websiteId=${selectedWebsite.websiteId}`,
                withCredentials: true
            });

            const redirectionMasterData = getRedirectionMasterEntries(response.data || {});
            const sourcePaths = extractRedirectionSourcePathsFromEntries(redirectionMasterData.entries);

            setExistingRedirectionEntries(redirectionMasterData.entries);
            setExistingRedirectionSourcePaths(sourcePaths);
            setHasLoadedExistingRedirectionEntries(true);

            return {
                entries: redirectionMasterData.entries,
                sourcePaths
            };
        } catch (error) {
            setExistingRedirectionEntries([]);
            setExistingRedirectionSourcePaths([]);
            setHasLoadedExistingRedirectionEntries(false);
            throw new Error(extractApiErrorMessage(error, "Unable to validate URL availability right now."));
        } finally {
            setIsLoadingExistingRedirectionEntries(false);
        }
    };

    useEffect(() => {
        let isCancelled = false;
        const fallbackNames = extractPageTemplateNamesFromPageTemplates(pageTemplates);

        setExistingPageTemplateNames(fallbackNames);
        setHasLoadedExistingPageTemplateNames(false);
        setAllowedExistingMicrositeName("");

        if (!selectedWebsite?.clientId || !selectedWebsite?.websiteId) {
            return undefined;
        }

        const syncExistingPageTemplateNames = async () => {
            try {
                const pageTemplateNames = await loadExistingPageTemplateNames();
                if (isCancelled) {
                    return;
                }

                setExistingPageTemplateNames(pageTemplateNames);
                setHasLoadedExistingPageTemplateNames(true);
            } catch (error) {
                if (!isCancelled) {
                    console.error("Error loading existing microsite names:", error);
                }
            }
        };

        syncExistingPageTemplateNames();

        return () => {
            isCancelled = true;
        };
    }, [pageTemplates, selectedWebsite?.clientId, selectedWebsite?.websiteId]);

    useEffect(() => {
        let isCancelled = false;

        setExistingRedirectionEntries([]);
        setExistingRedirectionSourcePaths([]);
        setHasLoadedExistingRedirectionEntries(false);

        if (!selectedWebsite?.clientId || !selectedWebsite?.websiteId) {
            return undefined;
        }

        const syncExistingRedirectionEntries = async () => {
            try {
                const redirectionData = await loadExistingRedirectionEntries();
                if (isCancelled) {
                    return;
                }

                setExistingRedirectionEntries(redirectionData.entries);
                setExistingRedirectionSourcePaths(redirectionData.sourcePaths);
                setHasLoadedExistingRedirectionEntries(true);
            } catch (error) {
                if (!isCancelled) {
                    console.error("Error loading existing microsite URLs:", error);
                }
            }
        };

        syncExistingRedirectionEntries();

        return () => {
            isCancelled = true;
        };
    }, [selectedWebsite?.clientId, selectedWebsite?.websiteId]);

    useEffect(() => {
        if (!fieldTouched.name) {
            return;
        }

        const duplicateNameError = getDuplicateMicrositeNameError({
            value: formData.name,
            existingNames: existingPageTemplateNames,
            allowedExistingName: allowedExistingMicrositeName
        });

        setFieldError("name", duplicateNameError || validateMicrositeName(formData.name));
    }, [allowedExistingMicrositeName, existingPageTemplateNames, fieldTouched.name, formData.name]);

    useEffect(() => {
        if (!fieldTouched.shortUrls) {
            return;
        }

        const duplicateUrlError = getDuplicateMicrositeUrlError({
            value: formData.shortUrls,
            existingSourcePaths: existingRedirectionSourcePaths
        });

        setFieldError("shortUrls", duplicateUrlError || parseMicrositeUrls(formData.shortUrls).errorMessage);
    }, [existingRedirectionSourcePaths, fieldTouched.shortUrls, formData.shortUrls]);

    const setFieldError = (fieldName, errorMessage) => {
        setFieldErrors((prevErrors) => ({
            ...prevErrors,
            [fieldName]: errorMessage
        }));
    };

    const markFieldTouched = (fieldName) => {
        setFieldTouched((prevTouched) => ({
            ...prevTouched,
            [fieldName]: true
        }));
    };

    const shouldShowFieldError = (fieldName) => {
        return Boolean(fieldTouched[fieldName] && fieldErrors[fieldName]);
    };

    const validateField = (fieldName, value) => {
        if (fieldName === "name") {
            return getDuplicateMicrositeNameError({
                value,
                existingNames: existingPageTemplateNames,
                allowedExistingName: allowedExistingMicrositeName
            }) || validateMicrositeName(value);
        }

        if (fieldName === "publishstoryMSID") {
            return validatePublishstoryMsid(value);
        }

        if (fieldName === "shortUrls") {
            return getDuplicateMicrositeUrlError({
                value,
                existingSourcePaths: existingRedirectionSourcePaths
            }) || parseMicrositeUrls(value).errorMessage;
        }

        return "";
    };

    const validateMicrositeNameAgainstExistingPages = async (value, nextAllowedExistingName = allowedExistingMicrositeName) => {
        const basicNameError = validateMicrositeName(value);
        if (basicNameError) {
            return basicNameError;
        }

        let namesToValidate = existingPageTemplateNames;

        if (!hasLoadedExistingPageTemplateNames && !isLoadingExistingPageTemplateNames) {
            try {
                namesToValidate = await loadExistingPageTemplateNames();
            } catch (error) {
                return extractApiErrorMessage(error, "Unable to validate microsite name right now.");
            }
        }

        return getDuplicateMicrositeNameError({
            value,
            existingNames: namesToValidate,
            allowedExistingName: nextAllowedExistingName
        });
    };

    const lookupMicrositeUrlInRedirectionMaster = async (value) => {
        const parsedUrls = parseMicrositeUrls(value);
        const normalizedSourcePath = normalizePathForComparison(parsedUrls.destinationPath);

        if (parsedUrls.errorMessage || !normalizedSourcePath || !selectedWebsite?.clientId || !selectedWebsite?.websiteId) {
            return null;
        }

        const lookupUrl = buildRedirectionLookupApiUrl({
            clientId: selectedWebsite.clientId,
            websiteId: selectedWebsite.websiteId,
            sourcePath: normalizedSourcePath
        });
        const nonceHeader = buildNonceHeaderFromApiUrl(lookupUrl);

        try {
            const response = await axios({
                method: "GET",
                url: lookupUrl,
                withCredentials: true,
                headers: nonceHeader ? { "p-nonce": nonceHeader } : {}
            });
            const lookupData = response?.data && typeof response.data === "object" ? response.data : null;
            const matchedSourcePath = normalizePathForComparison(lookupData?.sourcePath);
            const matchedDestinationPath = normalizePathForComparison(lookupData?.destinationPath);
            const hasMatchingLookupData = Boolean(matchedSourcePath || matchedDestinationPath || lookupData?._id);

            if (!hasMatchingLookupData) {
                return null;
            }

            if (!matchedSourcePath) {
                return lookupData;
            }

            return hasPathConflict(normalizedSourcePath, matchedSourcePath) ? lookupData : null;
        } catch (error) {
            if (error?.response?.status === 404) {
                return null;
            }

            throw error;
        }
    };

    const validateMicrositeUrlAgainstExistingEntries = async (value) => {
        const parsedUrls = parseMicrositeUrls(value);
        if (parsedUrls.errorMessage) {
            return {
                fieldError: parsedUrls.errorMessage
            };
        }

        let sourcePathsToValidate = existingRedirectionSourcePaths;

        if (!hasLoadedExistingRedirectionEntries && !isLoadingExistingRedirectionEntries) {
            try {
                const redirectionData = await loadExistingRedirectionEntries();
                sourcePathsToValidate = redirectionData.sourcePaths;
            } catch (error) {
                return {
                    fieldError: extractApiErrorMessage(error, "Unable to validate URL availability right now.")
                };
            }
        }

        const duplicateUrlError = getDuplicateMicrositeUrlError({
            value,
            existingSourcePaths: sourcePathsToValidate
        });

        if (duplicateUrlError) {
            return {
                fieldError: duplicateUrlError,
                parsedUrls
            };
        }

        try {
            const existingRedirectEntry = await lookupMicrositeUrlInRedirectionMaster(value);

            if (existingRedirectEntry) {
                return {
                    fieldError: DUPLICATE_MICROSITE_URL_ERROR,
                    existingRedirectEntry,
                    parsedUrls
                };
            }
        } catch (error) {
            return {
                fieldError: extractApiErrorMessage(error, "Unable to validate URL availability right now.")
            };
        }

        return {
            fieldError: "",
            parsedUrls
        };
    };

    const validateUrlAvailability = async (value) => {
        const urlValidationResult = await validateMicrositeUrlAgainstExistingEntries(value);
        if (urlValidationResult.fieldError) {
            return urlValidationResult;
        }

        const { parsedUrls } = urlValidationResult;

        return {
            fieldError: "",
            helperMessage: "URL is available.",
            existingTemplate: null,
            existingRedirectEntry: null,
            isExistingMicrosite: false,
            parsedUrls
        };
    };

    const postWithDetailedErrors = async ({ url, data, headers = {}, fallbackMessage }) => {
        try {
            return await axios({
                method: "POST",
                url,
                headers: {
                    "Content-Type": "application/json",
                    ...headers
                },
                withCredentials: true,
                data
            });
        } catch (error) {
            throw new Error(extractApiErrorMessage(error, fallbackMessage));
        }
    };

    const buildMicrositeDestinationPath = ({ sourcePath, micrositeName, publishstoryMsid, existingRedirectEntry }) => {
        const existingDestinationPath = normalizePathForComparison(existingRedirectEntry?.destinationPath);
        if (existingDestinationPath) {
            return existingRedirectEntry.destinationPath;
        }

        const normalizedSourcePath = normalizePathForComparison(sourcePath);
        const templateSegment = buildMicrositeTemplateSegment(micrositeName);
        const routeType = getMicrositeRouteType();
        const normalizedMsid = String(publishstoryMsid || "").trim();

        if (!normalizedSourcePath) {
            throw new Error("Microsite URL slug is missing.");
        }

        if (!templateSegment) {
            throw new Error("Microsite name is invalid for redirection path.");
        }

        if (!normalizedMsid) {
            throw new Error("Publishstory MSID is required for redirection.");
        }

        return `${normalizedSourcePath}/p/${templateSegment}/${routeType}/${normalizedMsid}.html`;
    };

    const saveMicrositeRedirection = async ({ sourcePath, micrositeName, publishstoryMsid, existingRedirectEntry }) => {
        const destinationPath = buildMicrositeDestinationPath({
            sourcePath,
            micrositeName,
            publishstoryMsid,
            existingRedirectEntry
        });

        const response = await postWithDetailedErrors({
            url: `${PAGE_BUILDER_ENDPOINTS.DOMAIN}/addURLRedirection`,
            data: {
                clientId: selectedWebsite.clientId,
                websiteId: selectedWebsite.websiteId,
                sourcePath,
                destinationPath,
                status: Number(formData.status) === 1,
                redirectionType: DEFAULT_REDIRECTION_TYPE,
                isSoftRedirection: true
            },
            fallbackMessage: "Failed to save URL redirection."
        });

        if (!response || response.status < 200 || response.status >= 300) {
            throw new Error("Failed to save URL redirection.");
        }
    };

    const handleInputChange = (name, value) => {
        setFormData((prevData) => ({
            ...prevData,
            [name]: value
        }));

        if (name === "shortUrls") {
            setUrlHelperMessage("");
            setAllowedExistingMicrositeName("");
        }

        if (fieldTouched[name]) {
            setFieldError(name, validateField(name, value));
        }
    };

    const isSaveMicrositeButton = (element) => {
        return Boolean(element?.closest?.(`[data-microsite-action="${SAVE_MICROSITE_ACTION}"]`));
    };

    const shouldSkipUrlAvailabilityCheckOnBlur = (event) => {
        return isSaveButtonPointerDownRef.current || isSaveMicrositeButton(event?.relatedTarget);
    };

    const setSaveButtonPointerDown = () => {
        isSaveButtonPointerDownRef.current = true;
    };

    const clearSaveButtonPointerDown = () => {
        isSaveButtonPointerDownRef.current = false;
    };

    const handleFieldBlur = async (fieldName, event) => {
        markFieldTouched(fieldName);

        if (fieldName === "name") {
            setFieldError("name", await validateMicrositeNameAgainstExistingPages(formData.name));
            return;
        }

        if (fieldName === "shortUrls") {
            const nextError = validateField(fieldName, formData.shortUrls);
            setFieldError(fieldName, nextError);
            setUrlHelperMessage("");
            setAllowedExistingMicrositeName("");

            if (nextError) {
                return;
            }

            if (shouldSkipUrlAvailabilityCheckOnBlur(event)) {
                return;
            }

            setIsCheckingUrls(true);
            const urlValidationResult = await validateUrlAvailability(formData.shortUrls);
            setIsCheckingUrls(false);

            if (urlValidationResult.fieldError) {
                setFieldError("shortUrls", urlValidationResult.fieldError);
                return;
            }

            setAllowedExistingMicrositeName("");
            setFieldError("shortUrls", "");
            setUrlHelperMessage(urlValidationResult.helperMessage || "");

            if (!selectedTemplate) {
                setFieldError("pageTemplateId", "No list or homepage template is available for this website.");
            } else {
                setFieldError("pageTemplateId", "");
            }

            return;
        }

        if (fieldName === "pageTemplateId") {
            if (!selectedTemplate) {
                setFieldError("pageTemplateId", "No list or homepage template is available for this website.");
            } else {
                setFieldError("pageTemplateId", "");
            }
            return;
        }

        setFieldError(fieldName, validateField(fieldName, formData[fieldName]));
    };

    const handleSave = async () => {
        clearSaveButtonPointerDown();

        if (!selectedWebsite?.clientId || !selectedWebsite?.websiteId) {
            showErrorToast("Unable to identify the current website. Please retry.");
            return;
        }

        setFieldTouched({
            name: true,
            publishstoryMSID: true,
            shortUrls: true,
            pageTemplateId: true
        });

        const nextFieldErrors = {
            name: validateField("name", formData.name),
            publishstoryMSID: validatePublishstoryMsid(formData.publishstoryMSID),
            shortUrls: validateField("shortUrls", formData.shortUrls),
            pageTemplateId: ""
        };

        setFieldErrors(nextFieldErrors);
        setUrlHelperMessage("");

        if (nextFieldErrors.name || nextFieldErrors.publishstoryMSID || nextFieldErrors.shortUrls) {
            return;
        }

        setIsCheckingUrls(true);
        const urlValidationResult = await validateUrlAvailability(formData.shortUrls);
        setIsCheckingUrls(false);

        if (urlValidationResult.fieldError) {
            setFieldError("shortUrls", urlValidationResult.fieldError);
            return;
        }

        setAllowedExistingMicrositeName("");

        const duplicateMicrositeNameError = await validateMicrositeNameAgainstExistingPages(formData.name, "");
        if (duplicateMicrositeNameError) {
            setFieldError("name", duplicateMicrositeNameError);
            return;
        }

        if (!selectedTemplate) {
            setFieldError("pageTemplateId", "No list or homepage template is available for this website.");
            return;
        }

        setFieldError("name", "");
        setFieldError("shortUrls", "");
        setFieldError("pageTemplateId", "");
        setUrlHelperMessage(urlValidationResult.helperMessage || "");

        setIsSaving(true);

        try {
            const { parsedUrls } = urlValidationResult;
            const normalizedPublishstoryMsid = formData.publishstoryMSID.toString().trim();
            const normalizedMicrositeName = formData.name.trim();
            const payload = {
                name: normalizedMicrositeName,
                type: "ArticleList",
                themeId: selectedTemplate.themeId,
                clientId: selectedWebsite.clientId,
                websiteId: selectedWebsite.websiteId,
                // Akamai WAF blocks raw <script> tags in request bodies; keep consistent with savePageTemplate.
                json: encodeToBase64(selectedTemplate.json),
                css: selectedTemplate.css,
                status: Number(formData.status),
                publishstoryMSID: normalizedPublishstoryMsid,
                shortUrls: parsedUrls.shortUrlPaths.join(","),
                destinationPath: parsedUrls.destinationPath,
                pageTemplateName: selectedTemplate.name || selectedTemplate.type,
                sourcePageId: selectedTemplate.pageId
            };

            if (!ADD_PAGE_TEMPLATE_AK) {
                throw new Error("Missing REACT_APP_ADD_PAGE_TEMPLATE_AK configuration.");
            }

            const response = await postWithDetailedErrors({
                url: PAGE_BUILDER_ENDPOINTS.DOMAIN + "/pageBuilder/addPageTemplate?env=2",
                headers: {
                    ak: ADD_PAGE_TEMPLATE_AK
                },
                data: payload,
                fallbackMessage: "Failed to create microsite."
            });

            if (!response || response.status !== 200) {
                throw new Error("Failed to create microsite.");
            }

            await saveMicrositeRedirection({
                sourcePath: parsedUrls.destinationPath,
                micrositeName: normalizedMicrositeName,
                publishstoryMsid: normalizedPublishstoryMsid,
                existingRedirectEntry: null
            });

            showSuccessToast("Microsite created successfully.");
            if (onMicrositeCreated) {
                onMicrositeCreated(response.data, payload.name);
            }
            handleCloseOverlay();
        } catch (error) {
            console.error("Error saving microsite:", error);
            showErrorToast(extractApiErrorMessage(error, "Unable to save microsite. Please try again later."));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <div className="Rectangle6">
                <div className="WelcomeToPublishstory">CREATE MICROSITE</div>
                <div className="ToCreateANewWebsitePleaseProvideTheFollowingInformation">
                    Create a custom microsite from an existing page template.
                </div>
                <FormControl fullWidth className="formControlWrapper" style={{ width: "490px" }}>
                    <p className="inputUpperText">Name<sup>*</sup></p>
                    <TextField
                        variant="filled"
                        className="customInputFiled"
                        placeholder="Microsite Name e.g. TimesOfIndia"
                        InputProps={{ style: { background: "white", borderRadius: "6px" } }}
                        value={formData.name}
                        onChange={(e) => handleInputChange("name", e.target.value)}
                        onBlur={(e) => handleFieldBlur("name", e)}
                        error={shouldShowFieldError("name")}
                        helperText={shouldShowFieldError("name") ? fieldErrors.name : ""}
                    />

                    <p className="inputUpperText">Publishstory_MSID<sup>*</sup></p>
                    <TextField
                        variant="filled"
                        className="customInputFiled"
                        placeholder="Publishstory MSID"
                        InputProps={{ style: { background: "white", borderRadius: "6px" } }}
                        value={formData.publishstoryMSID}
                        onChange={(e) => handleInputChange("publishstoryMSID", e.target.value)}
                        onBlur={(e) => handleFieldBlur("publishstoryMSID", e)}
                        error={shouldShowFieldError("publishstoryMSID")}
                        helperText={shouldShowFieldError("publishstoryMSID") ? fieldErrors.publishstoryMSID : ""}
                    />

                    <p className="inputUpperText">URL<sup>*</sup></p>
                    <TextField
                        variant="filled"
                        className="customInputFiled"
                        placeholder="Enter URL slug like /panamapapers"
                        InputProps={{ style: { background: "white", borderRadius: "6px" } }}
                        value={formData.shortUrls}
                        onChange={(e) => handleInputChange("shortUrls", e.target.value)}
                        onBlur={(e) => handleFieldBlur("shortUrls", e)}
                        error={shouldShowFieldError("shortUrls")}
                        FormHelperTextProps={{
                            sx: {
                                color: shouldShowFieldError("shortUrls") ? "#ff8a80" : "#8bd3a8",
                                marginLeft: 0,
                                marginRight: 0,
                                marginTop: "4px"
                            }
                        }}
                        helperText={
                            shouldShowFieldError("shortUrls")
                                ? fieldErrors.shortUrls
                                : (isCheckingUrls ? "Checking URL availability..." : urlHelperMessage)
                        }
                    />

                    <p className="inputUpperText">Status<sup>*</sup></p>
                    <Select
                        className="customInputFiled"
                        value={formData.status}
                        onChange={(e) => handleInputChange("status", e.target.value)}
                        style={{ backgroundColor: "white", textAlign: "-webkit-left", borderRadius: "6px", marginBottom: "20px" }}
                    >
                        <MenuItem value={1}>Active</MenuItem>
                        <MenuItem value={0}>Inactive</MenuItem>
                    </Select>

                    <p className="inputUpperText">Page Template<sup>*</sup></p>
                    <Select
                        className="customInputFiled"
                        value={selectedTemplate?.pageId || ""}
                        onChange={(e) => handleInputChange("pageTemplateId", e.target.value)}
                        onBlur={(e) => handleFieldBlur("pageTemplateId", e)}
                        displayEmpty
                        style={{ backgroundColor: "white", textAlign: "-webkit-left", borderRadius: "6px", marginBottom: "20px" }}
                    >
                        {listPageTemplates.length === 0 && (
                            <MenuItem value="" disabled>No list or homepage templates available</MenuItem>
                        )}
                        {listPageTemplates.map((template) => (
                            <MenuItem key={template.pageId} value={template.pageId}>
                                {template.name || template.type}
                            </MenuItem>
                        ))}
                    </Select>
                    {shouldShowFieldError("pageTemplateId") && (
                        <FormHelperText error style={{ marginTop: "-12px", marginBottom: "12px" }}>
                            {fieldErrors.pageTemplateId}
                        </FormHelperText>
                    )}

                    <Button
                        variant="contained"
                        onClick={handleSave}
                        onMouseDown={setSaveButtonPointerDown}
                        onMouseUp={clearSaveButtonPointerDown}
                        onMouseLeave={clearSaveButtonPointerDown}
                        onTouchStart={setSaveButtonPointerDown}
                        onTouchEnd={clearSaveButtonPointerDown}
                        onTouchCancel={clearSaveButtonPointerDown}
                        data-microsite-action={SAVE_MICROSITE_ACTION}
                        className="continueButton"
                        disabled={isSaving || isCheckingUrls}
                    >
                        {isSaving ? "Saving..." : "Save Microsite"}
                    </Button>
                </FormControl>
            </div>
            <div
                className="Clear"
                style={{ width: 64, height: 64, position: "absolute", right: 0, cursor: "pointer" }}
                onClick={handleCloseOverlay}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" fill="none">
                    <path d="M44.6263 40.8662C45.0168 41.2568 45.0168 41.8899 44.6263 42.2804L42.2805 44.6262C41.89 45.0168 41.2568 45.0168 40.8663 44.6262L32.0001 35.76L23.1339 44.6262C22.7433 45.0168 22.1102 45.0168 21.7196 44.6262L19.3739 42.2804C18.9833 41.8899 18.9833 41.2568 19.3739 40.8662L28.2401 32L19.3739 23.1338C18.9833 22.7433 18.9833 22.1101 19.3739 21.7196L21.7196 19.3738C22.1102 18.9833 22.7433 18.9833 23.1339 19.3738L32.0001 28.24L40.8663 19.3738C41.2568 18.9833 41.89 18.9833 42.2805 19.3738L44.6263 21.7196C45.0168 22.1101 45.0168 22.7433 44.6263 23.1338L35.7601 32L44.6263 40.8662Z" fill="white" />
                </svg>
            </div>
        </>
    );
};
